import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { MigrationLayout } from '../../components/MigrationLayout'
import { fetchMigrationDetails, executeMigration, validateMigrationState, getPartnerDisplayName, MigrationDetails } from '../../utils/migrationApi'
import { config } from '../../config'

export function LinkAccountPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [details, setDetails] = useState<MigrationDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [migrating, setMigrating] = useState(false)

  const state = searchParams.get('state')

  useEffect(() => {
    async function loadMigrationDetails() {
      try {
        // Basic client-side validation
        const validationError = validateMigrationState(state)
        if (validationError) {
          setError(validationError.message)
          if (validationError.redirectToHome) {
            setTimeout(() => {
              window.location.href = config.homeUrl
            }, 3000)
          }
          return
        }

        // Fetch details from API
        const migrationDetails = await fetchMigrationDetails(state!)
        setDetails(migrationDetails)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
        setError(errorMessage)
        
        // Auto-redirect to home after 3 seconds for most errors
        setTimeout(() => {
          window.location.href = config.homeUrl
        }, 3000)
      } finally {
        setLoading(false)
      }
    }

    loadMigrationDetails()
  }, [state])

  const handleConfirmMigration = async () => {
    if (!details || !state) return

    setMigrating(true)
    try {
      // executeMigration will either redirect on success or throw an error
      await executeMigration(details.partner, state)
      // If we get here, something unexpected happened
      navigate(`/link_error?error=server_error&partner=${encodeURIComponent(details.partner)}`)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong during account creation.'
      
      // Determine error type for redirect based on the error message
      let errorType = 'server_error'
      if (errorMessage.includes('already have a SWGRealms account') || errorMessage.includes('Try logging in instead')) {
        errorType = 'user_exists'
      } else if (errorMessage.includes("link isn't working")) {
        errorType = 'invalid_token'
      } else if (errorMessage.includes('migration link has expired') || errorMessage.includes('Missing validation cookie')) {
        errorType = 'expired_token'
      } else if (errorMessage.includes('contact support if this continues')) {
        errorType = 'server_error' // Infrastructure problems
      } else if (errorMessage.includes('Connection problem')) {
        errorType = 'server_error'
      }
      
      navigate(`/link_error?error=${errorType}&partner=${encodeURIComponent(details.partner)}`)
    } finally {
      setMigrating(false)
    }
  }

  const handleCancel = () => {
    window.location.href = config.homeUrl
  }

  if (loading) {
    return (
      <MigrationLayout>
        <div className="auth-page">
          <div className="auth-content">
            <div className="auth-card">
              <h1>Loading your account information...</h1>
              <p>Please wait while we verify your migration link.</p>
            </div>
          </div>
        </div>
      </MigrationLayout>
    )
  }

  if (error) {
    return (
      <MigrationLayout>
        <div className="auth-page">
          <div className="auth-content">
            <div className="auth-card">
              <h1>Oops!</h1>
              <p style={{ color: '#ff6b6b' }}>{error}</p>
              <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                Redirecting you back to the main site in a few seconds...
              </p>
            </div>
          </div>
        </div>
      </MigrationLayout>
    )
  }

  if (!details) {
    return (
      <MigrationLayout>
        <div className="auth-page">
          <div className="auth-content">
            <div className="auth-card">
              <h1>Unable to load account information</h1>
              <p>Please try refreshing the page or get a new link from SWGEmu.</p>
            </div>
          </div>
        </div>
      </MigrationLayout>
    )
  }

  return (
    <MigrationLayout>
      <div className="auth-page">
        <div className="auth-content">
          <div className="auth-card">
            <h1>Link Your Account</h1>
            <p>We found your {getPartnerDisplayName(details.partner)} account. Do you want to create a SWGRealms account with this information?</p>

            <div className="auth-explanation">
              <h3>Account Details</h3>
              <div className="account-details">
                <div className="detail-item">
                  <strong>Username:</strong> {details.username}
                </div>
                <div className="detail-item">
                  <strong>Email:</strong> {details.masked_email}
                </div>
                <div className="detail-item">
                  <strong>From:</strong> {getPartnerDisplayName(details.partner)}
                </div>
              </div>

              <div className="auth-note">
                <strong>Important:</strong> This will create your SWGRealms account and send you an email to set your password. 
                You'll need access to the email address shown above to complete the setup.
              </div>
            </div>

            <div className="auth-actions">
              <button
                className="auth-action-btn primary"
                onClick={handleConfirmMigration}
                disabled={migrating}
              >
                {migrating ? 'Creating your account...' : 'Yes, Create My Account'}
              </button>
              
              <button
                className="auth-action-btn secondary"
                onClick={handleCancel}
                disabled={migrating}
              >
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .auth-page {
          position: relative;
          min-height: calc(100vh - 80px);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: url('/swg_login_4x3.png') no-repeat center center fixed;
          background-size: cover;
          background-color: #000;
        }

        .auth-page::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.4);
          z-index: 1;
        }

        .auth-content {
          text-align: center;
          z-index: 10;
          position: relative;
        }

        .auth-card {
          background: rgba(10, 25, 47, 0.85);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(0, 212, 255, 0.3);
          border-radius: 12px;
          padding: 48px 40px;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.37),
            inset 0 0 20px rgba(0, 212, 255, 0.1);
          max-width: 600px;
        }

        .auth-card h1 {
          color: #ffffff;
          margin-bottom: 8px;
          font-size: 28px;
          font-weight: 300;
        }

        .auth-card p {
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 32px;
          font-size: 16px;
        }

        .auth-explanation {
          text-align: left;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          border: 1px solid rgba(0, 212, 255, 0.2);
        }

        .auth-explanation h3 {
          color: rgba(255, 255, 255, 0.9);
          margin-top: 0;
          margin-bottom: 20px;
          font-size: 18px;
          font-weight: 500;
        }

        .account-details {
          margin-bottom: 24px;
        }

        .detail-item {
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 16px;
          padding: 12px 16px;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(0, 212, 255, 0.2);
          border-radius: 6px;
          font-size: 16px;
          transition: all 0.3s ease;
        }

        .detail-item:hover {
          border-color: rgba(0, 212, 255, 0.4);
          background: rgba(0, 0, 0, 0.4);
        }

        .detail-item:last-child {
          margin-bottom: 0;
        }

        .detail-item strong {
          color: #00d4ff;
          display: inline-block;
          width: 80px;
          font-weight: 500;
        }

        .auth-note {
          background: rgba(0, 212, 255, 0.1);
          border-left: 3px solid #00d4ff;
          padding: 16px;
          border-radius: 6px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 14px;
          line-height: 1.5;
        }

        .auth-note strong {
          color: #00d4ff;
          font-weight: 500;
        }

        .auth-actions {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .auth-action-btn {
          border: none;
          padding: 14px 24px;
          font-size: 16px;
          font-weight: 500;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: block;
          width: 100%;
        }

        .auth-action-btn.primary {
          background: linear-gradient(135deg, #00a8cc 0%, #0088cc 100%);
          color: #ffffff;
        }

        .auth-action-btn.primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #00b8dc 0%, #0098dc 100%);
          box-shadow: 0 4px 15px rgba(0, 168, 204, 0.3);
        }

        .auth-action-btn.secondary {
          background: transparent;
          color: #00d4ff;
          border: 1px solid rgba(0, 212, 255, 0.3);
        }

        .auth-action-btn.secondary:hover:not(:disabled) {
          background: rgba(0, 212, 255, 0.1);
          border-color: rgba(0, 212, 255, 0.6);
        }

        .auth-action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .auth-card {
            margin: 0 20px;
            padding: 32px 24px;
            max-width: none;
          }
          
          .auth-card h1 {
            font-size: 24px;
          }
          
          .auth-explanation {
            padding: 20px;
          }

          .detail-item strong {
            width: 70px;
            font-size: 14px;
          }
        }
      `}</style>
    </MigrationLayout>
  )
}
